import {noop} from '@benchling/js-utils';
import {StyleSheet} from 'aphrodite';
import {NodeProps} from 'reactflow';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {REACT_FLOW_PRO_OPTIONS} from 'processes/graph-builder/ReactFlowConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MIN_ZOOM, MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';

import {ProcessFlowchartConfigEditorQuery} from './__generated/ProcessFlowchartConfigEditorQuery';
import {
  TaskSchemaNodeData,
  TaskSchemaInteractionNodeData,
  StaticFlowchartNodeData,
  FlowchartNode,
} from './processFlowchartConfigEditorNodeTypes';

export type ProcessFlowchartConfigGQL = ExtractByTypename<
  ProcessFlowchartConfigEditorQuery['processFlowchartConfig'],
  'ProcessFlowchartConfigNode'
>;
export type ProcessFlowchartConfigWithValidSchemaGQL = ProcessFlowchartConfigGQL & {
  schema: ExtractByTypename<ProcessFlowchartConfigGQL['schema'], 'ProcessTaskSchemaNode'>;
};
export type ProcessFlowchartConfigVersionGQL = ExtractByTypename<
  ProcessFlowchartConfigGQL['flowchartConfigVersion'],
  'ProcessFlowchartConfigVersionNode'
>;
export type ProcessFlowchartNodeGraphQL = ExtractByTypename<
  NonNullable<ProcessFlowchartConfigVersionGQL['templateFlowchart']>['nodeConfigs'][number],
  'ProcessFlowchartNodeConfigNode'
>;
export type InboundSchemaMapperGQL = ProcessFlowchartNodeGraphQL['inboundSchemaMappers'][number];
export type SchemaMapperDependencyGQL = ExtractByTypename<
  InboundSchemaMapperGQL['schemaMapperDependencies'][number],
  'ProcessFlowchartSchemaMapperDependencyNode'
>;
export type FieldMapperConfigGQL = ExtractByTypename<
  SchemaMapperDependencyGQL['fieldMapperConfigs'][number],
  'ProcessFlowchartSchemaFieldMapperConfigNode'
>;
export type ProcessTaskDefinitionVersionGraphQL = ExtractByTypename<
  ExtractByTypename<
    NonNullable<ProcessFlowchartNodeGraphQL['nodeDetails']>,
    'ProcessTaskNodeDetailsNode'
  >['processTaskDefinitionVersion'],
  'ProcessTaskDefinitionVersionNode'
>;
export type FieldDefinitionGraphQL = ExtractByTypename<
  ProcessTaskDefinitionVersionGraphQL['inputFieldDefinitions'][number],
  'ProcessFieldDefinitionNode'
>;

export interface NodeDetailsSidepanelContentProps {
  selectedNodeTaskDefinitionVersionSpec: TaskDefinitionVersionSpec;
  selectedFieldId: string | null;
  onChangeSelectedFieldId: (fieldId: string | null) => void;
  flowchart: TaskDefinitionVersionSpecFlowchart;
  locked?: boolean;
  onUpdateTaskDefinitionVersion: (
    newTaskDefinitionVersionSpec: TaskDefinitionVersionSpec,
    // If true, triggers immediate save. Otherwise, changes bundled with next manual/auto save
    isBlocking: boolean
  ) => void;
}

export enum NodeSettingsTabKeys {
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
  NODE_FIELD_DETAILS = 'NODE_FIELD_DETAILS',
  TASK_SCHEMA_DETAILS = 'TASK_SCHEMA_DETAILS',
}
export interface SidepanelTab {
  key: NodeSettingsTabKeys;
  label: string;
  content: React.ComponentType<NodeDetailsSidepanelContentProps>;
  openOnFieldSelect?: boolean;
}
/**
 * Grid spacing in pixels for the flowchart editor.
 * This is used to snap the nodes to a grid when dragging them around.
 * Larger numbers = further distance between snap points
 */
const SNAP_TO_GRID_PIXEL_SPACING = 100;
/**
 * Horizontal grid spacing (px), vertical grid spacing (px)
 */
const SNAP_GRID: [number, number] = [SNAP_TO_GRID_PIXEL_SPACING, SNAP_TO_GRID_PIXEL_SPACING];

export const DEFAULT_FLOWCHART_PROPS = {
  proOptions: REACT_FLOW_PRO_OPTIONS,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: true,
  zoomOnPinch: true,
  // This overrides any default viewport settings, initializes canvas with all nodes in view
  // Within the bounds of the min/max zoom levels
  fitView: true,
  snapToGrid: true,
  snapGrid: SNAP_GRID,
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
};

export const READ_ONLY_FLOWCHART_PROPS = {
  nodesDraggable: false,
  nodesConnectable: false,
  elementsSelectable: true,
  onConnect: noop,
};

export const EDITABLE_FLOWCHART_PROPS = {
  nodesDraggable: true,
  nodesConnectable: true,
  selectNodesOnDrag: false,
  elementsSelectable: true,
};

export const STANDARD_ROOT_NODE_NAME = 'Start';
export const STANDARD_ROOT_NODE_ICON: BenchlingIconName = 'start-node';
export const STANDARD_END_NODE_NAME = 'End';
export const STANDARD_END_NODE_ICON: BenchlingIconName = 'end-node';
export const TASK_SCHEMA_NODE_ICON = 'process_task_schema';

export enum ProcessFlowchartNodeType {
  TASK_SCHEMA_NODE = 'TaskSchemaNode',
  ROUTER_NODE = 'RouterNode',
  STOP_NODE = 'StopNode',
}

export enum ProcessFlowchartEdgeType {
  CONTROL_FLOW = 'control-flow',
  DATA_FLOW = 'data-flow',
}

export interface LogicNodeStaticData {
  id: string;
  type: ProcessFlowchartNodeType;
  sections: Array<unknown>;
}

export interface FlowchartStaticEdgeData {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  type: ProcessFlowchartEdgeType;
}

export interface FlowchartEdge extends FlowchartStaticEdgeData {}

export enum TaskSchemaNodeSectionType {
  INPUTS = 'Inputs',
  OUTPUTS = 'Outputs',
  NOT_MAPPABLE = 'Not Mappable',
}

export enum TaskSchemaNodeSection {
  INPUTS = 'Inputs',
  OUTPUTS = 'Outputs',
}

export enum ProcessFlowchartNodeSection {
  TASK_SCHEMAS = 'TASK_SCHEMAS',
  LOGIC = 'LOGIC',
  PIPELINES = 'PIPELINES',
}

/**
 * The source of the transition.
 *
 * This is used to determine which objects will be used as the input to the mapper at execution time.
 * In the backend, this defines which object (the task or output) is used as the "token" that is processed by
 * the workflow orchestration system.
 *
 * - If the source is "inputs", then we create a single downstream element for every input to the source node.
 *   In other words, the "process task" is used as the token in workflow orchestration.
 * - If the source is "outputs", then we create a single downstream element for every outupt of the source node.
 *   In other words, the "process output" is used as the token in workflow orchestration.
 */
export enum TransitionSource {
  OUTPUTS = 'outputs',
  INPUTS = 'inputs',
}

export interface CustomMappingData {
  lookupConfig?: LookupConfigSpec;
  resultMustBeNonNullToMap?: boolean;
}

export function isCustomMappingDataEmpty(customMappingData: CustomMappingData): boolean {
  return customMappingData.lookupConfig == null && !customMappingData.resultMustBeNonNullToMap;
}

export const FLOWCHART_TASK_SECONDARY_INFO = 'Flowchart task';
export const FLOWCHART_OUTPUT_SECONDARY_INFO = 'Flowchart output';

export const PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES = StyleSheet.create({
  // TODO: Remove this once transition modal constants are consolidated with this file
  editorBodyWrapper: {
    overflowY: 'hidden',
  },
  // TODO: Remove this once transition modal constants are consolidated with this file
  reactFlowCanvas: {
    display: 'flex',
    width: '100%',
    /**
     * Reactflow's parent must have a fixed height, but this component
     * gets used in a modal where the height is just 100%.
     * So we are using viewport height here to make it responsive while still a "fixed" height
     * The additional 250px is to account for the header and footer of the modal that will
     * also be taken up by the viewport.
     */
    height: 'calc(100vh - 250px)',
  },
  flowchartEditorContentWrapper: {
    display: 'flex',
    /**
     * Reactflow's parent must have a fixed height, but this component
     * gets used in a parent where the height is just 100%.
     * So we are using viewport height here to make it responsive while still a "fixed" height
     * The additional 250px is to account for additional components in the viewport.
     */
    height: 'calc(100vh - 250px)',
    width: '100%',
  },
  flowchartEditorContent: {
    display: 'flex',
    height: '100%',
    position: 'relative',
  },
});

// Allows for filtering out interaction fields from static node data
// When a new field is added to TaskSchemaInteractionNodeData, will get compilation error here
type InteractionKeys = keyof TaskSchemaInteractionNodeData;
const interactionFields: Record<InteractionKeys, undefined> = {
  onNodeHeaderClick: undefined,
  nodeDetailsSidepanelContent: undefined,
  onFieldClick: undefined,
  rowRenderer: undefined,
  nodeHeaderHandleRenderer: undefined,
};
export const INTERACTION_FIELDS = Object.keys(interactionFields);

export interface StaticTemplateFlowchart {
  nodesStaticData: Array<StaticFlowchartNodeData>;
  edgesStaticData: Array<FlowchartStaticEdgeData>;
}

export interface InteractiveTemplateFlowchart {
  nodes: Array<FlowchartNode>;
  edges: Array<FlowchartEdge>;
}

export interface TaskDefinitionVersionSpecEdge {
  fromNodeClientId: string;
  fromFieldDefinitionId?: string;
  toNodeClientId: string;
  toFieldDefinitionId?: string;
  type: ProcessFlowchartEdgeType;
}

export interface TaskDefinitionVersionSpecFlowchart {
  nodes: Record<string, TaskDefinitionVersionSpec>;
  edges: Array<TaskDefinitionVersionSpecEdge>;
}

export const NODE_SECTION_TO_HEADER_TITLE: Record<ProcessFlowchartNodeSection, string> = {
  [ProcessFlowchartNodeSection.TASK_SCHEMAS]: 'Task schemas',
  [ProcessFlowchartNodeSection.LOGIC]: 'Logic',
  [ProcessFlowchartNodeSection.PIPELINES]: 'Pipelines',
};
/**
 * Props for the task schema node renderer
 */

export type TaskSchemaNodeRendererProps = NodeProps<TaskSchemaNodeData>;

export interface SchemaMapperConfig {
  clientId: string;
  fromTaskDefinitionVersionIds: Array<string>;
  toTaskDefinitionVersionId: string;
}
